import { z } from 'zod';

/**
 * Tyre position layouts per axle configuration (gitaumoses4/chukua#176): how
 * many axles a truck class or trailer type has, and the named positions on
 * each (LF, RF, …). Axle number is the array index + 1 and slot number the
 * position's index within its axle + 1, so a gap can't be represented —
 * reordering an axle or a position is how one is moved.
 */

export const TYRE_LAYOUT_SUBJECT_KINDS = ['truck_class', 'trailer_type'] as const;
export type TyreLayoutSubjectKind = (typeof TYRE_LAYOUT_SUBJECT_KINDS)[number];

/**
 * What an axle does, which decides where it sits and what hangs off it in the
 * vehicle model: a truck's steer, drive and tag axles, a trailer's rear group
 * and a drawbar trailer's turntable axle. A new role is a migration (CHECK on
 * tyre_layout_axles).
 */
export const TRUCK_AXLE_ROLES = ['steer', 'drive', 'tag'] as const;
export const TRAILER_AXLE_ROLES = ['rear', 'turntable'] as const;
export const TYRE_LAYOUT_AXLE_ROLES = [...TRUCK_AXLE_ROLES, ...TRAILER_AXLE_ROLES] as const;
export type TyreLayoutAxleRole = (typeof TYRE_LAYOUT_AXLE_ROLES)[number];

export const AXLE_ROLES_BY_SUBJECT_KIND: Record<TyreLayoutSubjectKind, readonly TyreLayoutAxleRole[]> = {
  truck_class: TRUCK_AXLE_ROLES,
  trailer_type: TRAILER_AXLE_ROLES,
};

/** The role an axle has when none was given: a truck's first axle steers and the rest drive; a trailer's axles are its rear group. */
export function defaultAxleRole(subjectKind: TyreLayoutSubjectKind, axleIndex: number): TyreLayoutAxleRole {
  if (subjectKind === 'trailer_type') return 'rear';
  return axleIndex === 0 ? 'steer' : 'drive';
}

const TYRE_LAYOUT_MAX_AXLES = 12;
const TYRE_LAYOUT_MAX_POSITIONS_PER_AXLE = 8;

const tyreLayoutPositionInputSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(16)
    .transform((value) => value.toUpperCase()),
  name: z.string().trim().min(1).max(60),
});

const tyreLayoutAxleInputSchema = z.object({
  /** Which roles a subject allows is checked against its kind on save; omitted is `defaultAxleRole`. */
  role: z.enum(TYRE_LAYOUT_AXLE_ROLES).optional(),
  positions: z.array(tyreLayoutPositionInputSchema).min(1).max(TYRE_LAYOUT_MAX_POSITIONS_PER_AXLE),
});

export const tyreLayoutInputSchema = z
  .object({
    axles: z.array(tyreLayoutAxleInputSchema).min(1).max(TYRE_LAYOUT_MAX_AXLES),
  })
  .refine(
    (value) => {
      const codes = value.axles.flatMap((axle) => axle.positions.map((position) => position.code));
      return codes.length === new Set(codes).size;
    },
    { message: 'Position codes must be unique across the layout', path: ['axles'] },
  );
export type TyreLayoutInput = z.infer<typeof tyreLayoutInputSchema>;

export interface TyreLayoutPositionView {
  code: string;
  name: string;
  axleNumber: number;
  slotNumber: number;
}

export interface TyreLayoutAxleView {
  axleNumber: number;
  role: TyreLayoutAxleRole;
  positions: Omit<TyreLayoutPositionView, 'axleNumber'>[];
}

export interface TyreLayout {
  subjectKind: TyreLayoutSubjectKind;
  subjectCode: string;
  axles: TyreLayoutAxleView[];
}
